import { CircularProgress, Stack, Typography } from '@mui/material';

import type { DefinitionResponse, SubjectLanguage } from '../types';

export interface MaterializationSummaryProps {
  definition: DefinitionResponse | undefined;
  isChecking: boolean;
  subject: SubjectLanguage;
}

const CountRow = ({ value, children }: { value: number; children: string }) => (
  <Stack direction="row" spacing={1.5} sx={{ alignItems: 'baseline' }}>
    <Typography variant="h6" component="span" sx={{ minWidth: 48, textAlign: 'right' }}>
      {value.toLocaleString()}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  </Stack>
);

/**
 * The activation confirmation for AC-21: a visible "checking existing records"
 * state that resolves into the satisfied/open counts.
 *
 * Counts render only once the definition reports `done`. While it is checking
 * they are null on the DTO, and showing them as 0 would read as "nobody has a
 * card" — the opposite of what an in-progress evaluation means.
 */
export const MaterializationSummary = ({
  definition,
  isChecking,
  subject,
}: MaterializationSummaryProps) => {
  if (isChecking || !definition) {
    return (
      <Stack
        direction="row"
        spacing={1.5}
        role="status"
        aria-live="polite"
        sx={{ alignItems: 'center', py: 2 }}
      >
        <CircularProgress size={18} />
        <Stack>
          <Typography variant="body2">Checking existing records…</Typography>
          <Typography variant="caption" color="text.secondary">
            We are looking at every active {subject.singular} to see who already meets
            this requirement. You can close this — it finishes on its own.
          </Typography>
        </Stack>
      </Stack>
    );
  }

  const satisfied = definition.materialization_born_satisfied ?? 0;
  const open = definition.materialization_born_open ?? 0;
  const alreadyHad = definition.materialization_already_had ?? 0;

  return (
    <Stack spacing={1.5} role="status" aria-live="polite" sx={{ py: 1 }}>
      <Typography variant="body2">
        Checked {(definition.materialization_panel_size ?? satisfied + open).toLocaleString()}{' '}
        active {subject.plural}. No messages were sent.
      </Typography>

      <CountRow value={satisfied}>already meet this requirement</CountRow>
      <CountRow value={open}>still need it</CountRow>
      {alreadyHad > 0 && <CountRow value={alreadyHad}>already had this requirement</CountRow>}

      <Typography variant="caption" color="text.secondary">
        Send requests from a {subject.singular}&apos;s record, or from the requirements
        center.
      </Typography>
    </Stack>
  );
};
